import React, { useEffect, useState } from "react";
import client, { formatApiError } from "@/lib/api";
import { ScrollText, RefreshCw } from "lucide-react";
import AdminPageHeader from "@/components/admin/AdminPageHeader";
import { AdminFilterSelect } from "@/components/admin/AdminFilterBar";
import AdminButton from "@/components/admin/AdminButton";
import AdminEmptyState from "@/components/admin/AdminEmptyState";

const PILL_TONES = ["success", "destructive", "warning", "accent"];
function pillTone(action) {
  let hash = 0;
  for (let i = 0; i < action.length; i++) hash = (hash * 31 + action.charCodeAt(i)) | 0;
  return PILL_TONES[Math.abs(hash) % PILL_TONES.length];
}

const TONE_CLS = {
  success: "bg-hv-tealTint text-hv-teal",
  destructive: "bg-hv-coralTint text-hv-coral",
  warning: "bg-hv-goldTint text-hv-navy",
  accent: "bg-hv-navyTint text-hv-navy",
};

export default function AdminAuditLog() {
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [actionFilter, setActionFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "200" });
      if (actionFilter) params.set("action", actionFilter);
      const { data } = await client.get(`/admin/audit-log?${params.toString()}`);
      setEntries(data.entries);
      setTotal(data.total);
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [actionFilter]); // eslint-disable-line

  const actions = [...new Set(entries.map((e) => e.action))].sort();

  return (
    <div data-testid="admin-audit-log">
      <AdminPageHeader
        icon={ScrollText}
        title="Audit log"
        subtitle={`${total} entries · every admin action, who did it, and when`}
        actions={<AdminButton variant="secondary" icon={RefreshCw} onClick={load}>Refresh</AdminButton>}
      />

      <div className="flex flex-wrap items-center gap-2">
        <AdminFilterSelect value={actionFilter} onChange={(e) => setActionFilter(e.target.value)}>
          <option value="">All actions</option>
          {actions.map((a) => <option key={a} value={a}>{a}</option>)}
        </AdminFilterSelect>
      </div>

      {err && <div className="mt-4 text-hv-coral font-bold text-sm">{err}</div>}

      {loading ? (
        <p className="mt-6 text-hv-slate text-sm">Loading…</p>
      ) : entries.length === 0 ? (
        <div className="mt-6">
          <AdminEmptyState icon={ScrollText} title="No activity yet." />
        </div>
      ) : (
        <ul className="mt-6 space-y-2">
          {entries.map((e) => (
            <li key={e.id} className="rounded-2xl bg-white border border-[#E5DFC8] p-4 flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3 flex-wrap min-w-0">
                <span className={`rounded-full px-3 py-1 text-xs font-bold ${TONE_CLS[pillTone(e.action)]}`}>{e.action}</span>
                <span className="text-sm text-hv-navy font-semibold truncate">
                  {e.target_type}{e.target_id ? `:${e.target_id}` : ""}
                </span>
                <span className="text-sm text-hv-slate truncate">{e.actor_email || e.actor_id} ({e.actor_role})</span>
                {e.details && Object.keys(e.details).length > 0 && (
                  <span className="text-xs text-hv-slate truncate">{JSON.stringify(e.details)}</span>
                )}
              </div>
              <span className="text-xs text-hv-slate shrink-0">{new Date(e.created_at).toLocaleString()}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
